import '../LoiComparisonSummary/LoiComparisonSummary.scss';
import './LoiHistoryPreviewPopup.scss';
import React, { useEffect, useState } from 'react';
import ReactSwitch from 'react-switch';
import { ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../../components/Rodal/Rodal';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import { LoiForm } from '../../../../slices/marketplace/loi-form/loi-form';
import { fetchLoiForm } from '../../../../slices/marketplace/loi-form/loi-forms-api';
import { hasFairValueColumn, isSfas } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { useStateSelector } from '../../../../store/selectors';
import { BuyerSummaryCard, CovenantsValueColumn } from '../LoiComparisonSummary/BuyerSummaryCard';
import { SfasFeeParams } from '../../../../slices/marketplace/loi-comparison/consideration-fees';
import { LabelsColumn, VvConnectFeesLabelsColumn, CovenantsLabelsColumn } from '../LoiComparisonSummary/LoiComparisonSummary';
import { VvConnectFeesSection } from '../LoiComparisonSummary/VvConnectFeesSection';
import { TypesOfConsiderationSection } from '../LoiComparisonSummary/TypesOfConsiderationSection';
import { buildConsiderationSummary } from '../LoiComparisonSummary/LoiComparisonRows';
import { getFairValuesByType, getFaceValuesByType } from '../../../../slices/marketplace/loi-comparison/consideration-fair-values';
import { DeepDiveDetailPanel } from '../LoiComparisonSummary/DeepDiveDetailPanel/DeepDiveDetailPanel';
import { getFirstDeepDiveRowLabel } from '../LoiComparisonSummary/DeepDiveDetailPanel/detail-config';
import { LoiComparisonLegalIssues } from '../LoiComparisonLegalIssues/LoiComparisonLegalIssues';

const POPUP_WIDTH = 1000;

type PreviewTabId = 'loiSummary' | 'legalIssues' | 'covenants';

interface PreviewTab {
    id: PreviewTabId;
    label: string;
}

const PREVIEW_TABS: PreviewTab[] = [
    { id: 'loiSummary', label: 'LOI Summary' },
    { id: 'legalIssues', label: 'Legal Issues' },
    { id: 'covenants', label: 'Additional NDA Covenants' },
];

interface SelectedDetail {
    rowLabel: string;
    buyerId: number;
}

export interface LoiHistoryPreviewPopupProps {
    isVisible: boolean;
    onClose: () => void;
    buyerItem: LoiComparisonFormDto;
    historicalLoiFormId: string;
    engagementId: number;
    levelOfService: number;
    sfasParams?: SfasFeeParams | null;
}

export const LoiHistoryPreviewPopup = (props: LoiHistoryPreviewPopupProps) => {
    const { isVisible, onClose, buyerItem, historicalLoiFormId, levelOfService, sfasParams = null } = props;
    const axios = useStateSelector((state) => state.core.axios);
    const [historicalForm, setHistoricalForm] = useState<LoiForm | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<PreviewTabId>('loiSummary');
    const [isDeepDive, setIsDeepDive] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState<SelectedDetail | null>(null);
    const [collapsibleColumns, setCollapsibleColumns] = useState<Record<string, boolean>>({ face: false, fair: false });

    const showFairValue = hasFairValueColumn(levelOfService);

    useEffect(() => {
        if (!historicalLoiFormId || !axios) {
            return;
        }
        setIsLoading(true);
        setHistoricalForm(null);
        fetchLoiForm(axios, historicalLoiFormId)
            .then((form) => setHistoricalForm(form))
            .finally(() => setIsLoading(false));
    }, [historicalLoiFormId]);

    const historicalItem: LoiComparisonFormDto = historicalForm
        ? { ...buyerItem, form: historicalForm }
        : buyerItem;

    const readonlyItem = { ...historicalItem, isResubmissionToggleVisible: false, isSelected: false };

    const handleModeSwitchChange = (checkedTowardNetToOwner: boolean) => {
        const nextIsDeepDive = !checkedTowardNetToOwner;
        setIsDeepDive(nextIsDeepDive);
        if (!nextIsDeepDive) {
            setSelectedDetail(null);
            return;
        }
        const rowLabel = getFirstDeepDiveRowLabel(readonlyItem.form?.termForm?.purchasePriceSummary);
        setSelectedDetail(rowLabel ? { rowLabel, buyerId: readonlyItem.buyerId } : null);
    };

    const handleCellsClick = (rowLabel: string, buyerId: number) => {
        setSelectedDetail(prev =>
            prev?.rowLabel === rowLabel && prev?.buyerId === buyerId
                ? null
                : { rowLabel, buyerId }
        );
    };

    const toggleColumn = (key: string) =>
        setCollapsibleColumns(prev => ({ ...prev, [key]: !prev[key] }));

    const renderLoiSummary = () => (
        <div className="loi-comparison-summary-wrapper">
            <div className="deep-dive-toolbar">
                <div className="switch-container">
                    <div className="switch-option">
                        <span>Deep Dive</span>
                        <InfoTooltip
                            text="Show details for each Purchase Price summary row. Click on (highlight) each form of consideration in the summary in order to see the details for that form of consideration below."
                            idText="loi-history-deep-dive-tooltip"
                        />
                    </div>
                    <ReactSwitch
                        checked={!isDeepDive}
                        onChange={handleModeSwitchChange}
                        height={22}
                        handleDiameter={16}
                        onColor="#535bd6"
                        offColor="#535bd6"
                        checkedIcon={false}
                        uncheckedIcon={false}
                    />
                    <div className="switch-option">
                        <span>Net to Owner</span>
                        <InfoTooltip
                            text="Show all sections of the LOI comparison. Provide fee payment selection for each form of consideration and projected values at time of receipt in order to create the appropriate analysis."
                            idText="loi-history-net-to-owner-tooltip"
                        />
                    </div>
                </div>
            </div>
            <div className="loi-comparison-summary">
                <div className="summary-inner">
                    <LabelsColumn />
                    <div className="buyers-data-area">
                        <BuyerSummaryCard
                            item={readonlyItem}
                            levelOfService={levelOfService}
                            sfasParams={sfasParams}
                            showFairValue={showFairValue}
                            collapsibleColumns={collapsibleColumns}
                            onToggleColumn={toggleColumn}
                            isDeepDive={isDeepDive}
                            selectedRowLabel={selectedDetail?.buyerId === readonlyItem.buyerId ? (selectedDetail?.rowLabel ?? null) : null}
                            onCellsClick={handleCellsClick}
                            canSelectThisBuyer={false}
                            onSelectClick={() => {}}
                            onResubmissionToggle={() => {}}
                            tooltipIdSuffix="-preview"
                        />
                    </div>
                </div>
                {!isDeepDive && (
                    <div className="summary-inner types-of-consideration-section">
                        <TypesOfConsiderationSection
                            items={[{
                                buyerId: readonlyItem.buyerId,
                                buyerName: readonlyItem.buyerName,
                                considerationRows: readonlyItem.considerationRows,
                                fairValueByType: getFairValuesByType(readonlyItem),
                                faceValueByType: getFaceValuesByType(readonlyItem),
                            }]}
                            canEditTiming={false}
                            canEditCashFlow={false}
                            canEditComment={false}
                            levelOfService={levelOfService}
                            onSave={() => {}}
                        />
                    </div>
                )}
                {!isDeepDive && (
                    <div className="summary-inner covenants-section">
                        <CovenantsLabelsColumn />
                        <div className="buyers-data-area">
                            <div className="buyer-summary-card">
                                <CovenantsValueColumn item={readonlyItem} tooltipIdSuffix="-preview-cov" />
                            </div>
                        </div>
                    </div>
                )}
                {!isDeepDive && (() => {
                    const considerationSummary = buildConsiderationSummary(historicalItem, levelOfService, sfasParams);
                    return (
                        <div className="summary-inner vv-fees-section">
                            <VvConnectFeesLabelsColumn isSfasEngagement={isSfas(levelOfService)} />
                            <div className="buyers-data-area">
                                <div className="buyer-summary-card">
                                    <VvConnectFeesSection
                                        fees={historicalItem.vvConnectFees}
                                        portionPaidAtClose={considerationSummary.portionPaidAtClose}
                                        projectedTransactionValue={considerationSummary.projectedTransactionValue}
                                        projectedTransactionFee={considerationSummary.projectedTransactionFee}
                                        levelOfService={levelOfService}
                                        sfasParams={sfasParams}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
            {isDeepDive && selectedDetail !== null && (
                <div className="deep-dive-details-section">
                    <div className="deep-dive-details-inner">
                        <div className="deep-dive-section-divider" />
                        <div className="deep-dive-detail-header">
                            <div className="deep-dive-detail-header__title">Details</div>
                            <div className="deep-dive-detail-header__subtitle">
                                <strong>Buyer:</strong> {historicalItem.buyerName}
                            </div>
                            <div className="deep-dive-detail-header__subtitle">
                                <strong>Consideration:</strong> {selectedDetail.rowLabel}
                            </div>
                        </div>
                        <div className="deep-dive-section-divider" />
                        <DeepDiveDetailPanel
                            rowLabel={selectedDetail.rowLabel}
                            buyer={historicalItem}
                        />
                    </div>
                </div>
            )}
        </div>
    );

    const renderTabContent = () => {
        if (isLoading) {
            return <div className="loi-history-preview-loading">Loading...</div>;
        }

        switch (activeTab) {
            case 'loiSummary':
                return renderLoiSummary();
            case 'legalIssues':
                return <LoiComparisonLegalIssues items={[historicalItem]} mode="legalIssues" showBuyerSubTabs={false} />;
            case 'covenants':
                return <LoiComparisonLegalIssues items={[historicalItem]} mode="covenants" showBuyerSubTabs={false} />;
            default:
                return null;
        }
    };

    return (
        isVisible && (
            <Rodal
                visible={true}
                animation="fade"
                showMask={true}
                width={POPUP_WIDTH}
                onClose={onClose}
                className="loi-history-preview-popup">
                <ModalHeader>
                    LOI Preview — {buyerItem.buyerName}
                </ModalHeader>
                <ModalBody>
                    <div className="body_inner_content">
                        <div className="RRT__container">
                            <div className="RRT__tabs body-tabs">
                                {PREVIEW_TABS.map((tab) => (
                                    <div
                                        key={tab.id}
                                        className={activeTab === tab.id
                                            ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                            : 'RRT__tab'}
                                        onClick={() => setActiveTab(tab.id)}>
                                        {tab.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                        {renderTabContent()}
                    </div>
                </ModalBody>
            </Rodal>
        )
    );
};
